import { FeatureFlag } from '@ps/web-core-enums';

import { RequestError } from '@swf/core/errors/RequestError';
import type { WidgetEffectParams } from '@swf/core/store';
import { pageChange } from '@swf/core/utils/actions';
import buildSportsRequest from '@swf/sports/utils/buildSportsRequest';
import { generateSportsPath } from '@swf/sports/utils/generateSportsPath';
import getErrorStatusCode from '@swf/sports/utils/getErrorStatusCode';
import { getWidgetCancellationSignal } from '@swf/sports/utils/getWidgetCancellationSignal';

import type { SportsSeoWidgetConfig } from '../config';

import type {
  FetchSuccessPayload,
  MetaResponsePayload,
  WidgetDispatch,
  WidgetSelect
} from './types';

export const effects = {
  async preload(_payload: undefined, params: WidgetEffectParams<SportsSeoWidgetConfig>) {
    const widgetSelect: WidgetSelect = params.select[params.widget.uid];
    const widgetDispatch: WidgetDispatch = params.dispatch[params.widget.uid];

    pageChange(params.actions)?.subscribe({
      next: () => {
        widgetDispatch.fetchWidgetData();
      }
    });

    if (!widgetSelect.hasPreloaded(params.rootState)) {
      await effects.fetchWidgetData(undefined, params);
    }
  },

  async fetchWidgetData(_payload: undefined, params: WidgetEffectParams<SportsSeoWidgetConfig>) {
    const { dispatch, select, widget, rootState, request } = params;
    const urlParams = select.sports.getUrlParams(rootState);
    const page = select.cms.getPage(rootState);
    const aliases = select.cms.getAliases(rootState);
    const sportsRequest = buildSportsRequest(request, rootState);
    const locale = select.locale.get(rootState);
    const areDynamicAlternatesEnabled = select.feature.check(
      rootState,
      FeatureFlag.ISPDynamicAlternates
    );
    const alias = page?.alias;
    const widgetDispatch: WidgetDispatch = dispatch[widget.uid];

    if (!widget.apiPath) return;

    try {
      const { body } = await sportsRequest<MetaResponsePayload>({
        path: widget.apiPath,
        body: urlParams,
        bypassBffCache: false,
        signal: getWidgetCancellationSignal(params)
      });

      const apiTokens = body.tokens[locale] ?? {};
      const renamedTokens = Object.fromEntries(
        Object.entries(widget.translationsMap || {}).map(([tagVar, apiVar]) => [
          tagVar,
          apiVar ? apiTokens[apiVar] : undefined
        ])
      );
      const translatedTokens = { ...apiTokens, ...renamedTokens };

      const { alternates: alternatesResponse } = body;
      const payload: FetchSuccessPayload = {
        tokens: translatedTokens,
        alternates: []
      };

      if (alias) {
        const path = aliases[alias];

        if (path) {
          payload.actualPath = generateSportsPath(path, urlParams);
          payload.expectedPath = generateSportsPath(path, apiTokens);

          if (areDynamicAlternatesEnabled && alternatesResponse) {
            alternatesResponse.forEach(({ hrefLang, tokens: alternatesTokens, url: baseUrl }) => {
              const url = new URL(baseUrl);
              url.pathname += generateSportsPath(path, alternatesTokens);
              url.pathname = url.pathname.replace('//', '/');
              payload.alternates.push({
                hrefLang,
                href: url.toString()
              });
            });
          }
        }
      }

      widgetDispatch.fetchWidgetDataSuccess(payload);
    } catch (error) {
      const { redirectStatusCode, redirectTo } = widget;
      if (error instanceof RequestError && getErrorStatusCode(error) === 404 && redirectTo) {
        const path = aliases[redirectTo];
        if (!path) return;

        widgetDispatch.fetchWidgetDataFailure({
          redirect: {
            statusCode: redirectStatusCode ?? 301,
            url: generateSportsPath(path, urlParams)
          }
        });
      }
    }
  }
};
